import { useTheme } from 'styled-components';

import { Paragraph } from '@/core/components/Typography';
import { Icon } from '@/core/components/Icons';

import { normalizeSourceForDisplay } from '@/features/Reports/helpers';

import { ArticleCard } from './ArticleCard';
import {
  ArticlesContainer,
  ArticlesList,
  CountBadge,
  CountDelta,
  HeaderMeta,
  HeaderTitle,
  RefreshButton,
  RefreshStatusText,
  SectionHeader,
} from './ArticlesSection.styles';
import { ArticlesSectionProps } from './ArticlesSection.types';
import { useRefreshButtonSuccess } from './useRefreshButtonSuccess';
import { useRefreshStatusLabel } from './useRefreshStatusLabel';

const formatCountDelta = (delta: number): string => {
  if (delta > 0) {
    return `+${delta}`;
  }

  if (delta < 0) {
    return `${delta}`;
  }

  return '+0';
};

export const ArticlesSection = ({
  sources,
  refreshStatus,
  refreshUpdatedAt,
  countDelta,
  onRefresh,
}: ArticlesSectionProps) => {
  const { colors } = useTheme();
  const isRefreshing = refreshStatus === 'loading';
  const showButtonSuccess = useRefreshButtonSuccess(refreshStatus);
  const refreshStatusLabel = useRefreshStatusLabel(refreshStatus, refreshUpdatedAt);

  const formattedCountDelta = countDelta !== null ? formatCountDelta(countDelta) : null;

  const sortedSources = [...sources].sort((a, b) => {
    const dateA = a.publishedDatetime ? new Date(a.publishedDatetime).getTime() : 0;
    const dateB = b.publishedDatetime ? new Date(b.publishedDatetime).getTime() : 0;

    return dateB - dateA;
  });

  return (
    <ArticlesContainer>
      <SectionHeader>
        <HeaderTitle>
          <CountBadge>{sources.length}</CountBadge>
          {formattedCountDelta && (
            <CountDelta $delta={countDelta ?? 0}>{formattedCountDelta}</CountDelta>
          )}
          <Paragraph type="bold">Articles</Paragraph>
        </HeaderTitle>
        <HeaderMeta>
          {refreshStatusLabel && (
            <RefreshStatusText $status={refreshStatus}>{refreshStatusLabel}</RefreshStatusText>
          )}
          <RefreshButton
            type="button"
            onClick={onRefresh}
            disabled={isRefreshing}
            $success={showButtonSuccess}
            aria-label="Update articles"
          >
            <Icon
              name={showButtonSuccess ? 'Check' : 'Refresh'}
              size={14}
              color={showButtonSuccess ? colors.reliability.high : undefined}
            />
          </RefreshButton>
        </HeaderMeta>
      </SectionHeader>
      {sources.length ? (
        <ArticlesList>
          {sortedSources.map((source, index) => {
            const article = normalizeSourceForDisplay(source, index);

            return (
              <ArticleCard
                key={`${article.id}-${article.url}`}
                url={article.url}
                title={article.title}
                name={article.name}
                credibility={article.credibility}
                publishedDatetime={article.publishedDatetime}
                image={article.image}
              />
            );
          })}
        </ArticlesList>
      ) : (
        <Paragraph
          type="normal"
          color={colors.secondaryText}
        >
          No articles available.
        </Paragraph>
      )}
    </ArticlesContainer>
  );
};
